'use client';

import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import {
  DataGridPremium,
  GridColumnMenu,
  GridColumnMenuProps,
  GridRowId,
  GridRowSelectionModel,
  GridSortDirection,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import {
  listTableColumns,
  TableCheckbox,
  TableFooter,
  TableSettings,
  ListsUploaderDrawer,
  EncountersToolbar,
  EditFilterDrawer,
  NewNoVolumeOverlay,
} from 'src/components';
import { CircularProgress } from 'src/components/PulseUI';

import useAuthorization from 'src/hooks/authorization/useAuthorization';
import { useGetMappingsQuery } from 'src/api/redux';
import useLocalStorage from 'src/hooks/system/useLocalStorage';
import useEncounterColumns from 'src/hooks/table/useEncounterColumns';
import useLists from 'src/hooks/lists/useLists';
import useTableFilterSelections, { extractBaseId, resolveVolumeSortField } from 'src/hooks/filters/useTableFilterSelections';
import { LOCATION } from 'src/hooks/filters/constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  useGetProviderListMembersQuery,
  useExportProviderListMembersMutation,
} from 'src/api/redux/lists/listsApi';
import { getSearchQueryParamsForAPI } from 'src/utils/url.helpers';
import { hasSitesOfCare } from 'src/utils/hasSitesOfCare/hasSitesOfCare';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useProspectSelection } from 'src/context/prospects';
import { VOLUME_SELECT_OPTIONS } from 'src/components/constants';
import { TableVolumeDrawer } from 'src/components/TableDrawers';
import { useDebounce } from 'use-debounce';
import {
  useScoutContext,
  toScoutSelection,
  SCOUT_CONTEXT_KEYS,
} from 'src/hooks/scout';

const EMPTY_COLUMN_IDS: string[] = [];
const EMPTY_ROWS: any[] = [];

const NewListsTable = () => {
  const hasLoaded = useRef(false);
  const prevFlagRef = useRef<boolean | undefined>(undefined);
  const previousColumnIdsRef = useRef<string[]>([]);
  const sortModelRef = useRef([]);
  const log = useLogging();
  const { setNotification } = useNotification();
  const apiRef = useGridApiRef();
  const auth = useAuthorization();
  const { showEncountersTotals } = useFlags();

  const {
    columnIds, // this is an array of volume ids [number, number, number]
    isTrending,
    date_range,
    setColumnId,
    isInitialized,
    apiVolumeFilterFormat,
    editingColumnId, // Add this to detect when we're editing vs creating
  } = useTableFilterSelections(LOCATION.LISTS);

  // Use the hook with the specific table type and pass columnIds
  const { volumeColumns, optionColumns, createSecondaryTableColumns } =
    useEncounterColumns(LOCATION.LISTS, {
      setColumnId,
      isTrending,
      columnIds: isInitialized ? columnIds : EMPTY_COLUMN_IDS,
    });
  const { selectedList } = useLists();

  const { data: allMappings } = useGetMappingsQuery({
    sort: 'label',
    order: 'asc',
    page: 0,
    pageSize: 100,
  });

  const currentYears = useMemo(() => {
    if (!date_range || !date_range?.gte || !date_range?.lte) return [];

    const years = [];
    const { gte, lte } = date_range;
    for (let year = Number(gte); year <= Number(lte); year++) {
      years.push(year);
    }

    return years;
  }, [date_range]);

  const crmTypes = useMemo(() => {
    return Array.from(
      new Set(
        allMappings?.results
          ?.filter((mapping) => mapping.enabled)
          .map((mapping) => mapping.crm?.toLowerCase())
      )
    );
  }, [allMappings]);

  const {
    state: [, setNewProspects],
  } = useProspectSelection();

  const [search, setSearch] = useState('');
  const [debounceSearch] = useDebounce(search, 500);
  const [isExporting, setIsExporting] = useState(false);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    []
  );
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [exportMembers] = useExportProviderListMembersMutation();

  // Get filter format from table filter selections
  const { apiFilterFormat } = useTableFilterSelections(LOCATION.LISTS);

  const [sort, setSort] = useState({
    sort: 'name',
    sort_id: null,
    sort_field: null,
    order: 'asc',
    sortModel: [], // Add sortModel to track DataGrid state
  });

  // Create initial sort model based on columnIds
  const initialSortModel = useMemo(() => {
    if (hasLoaded.current === true) return;
    // Only set initial sort if we have columnIds and they're initialized
    if (columnIds?.length > 0 && isInitialized) {
      const firstColumnId = columnIds[0];

      return [
        { field: firstColumnId.toString(), sort: 'desc' as GridSortDirection },
      ];
    }
    return [{ field: 'name', sort: 'desc' as GridSortDirection }]; // Default sort
  }, [columnIds, isInitialized]);

  // Handle when columnIds change after initial render
  useEffect(() => {
    const columnIdsChanged =
      JSON.stringify(previousColumnIdsRef.current) !==
      JSON.stringify(columnIds);

    // Recompute the default volume sort when the flag resolves/changes so ordering
    // stays aligned with the (encounters vs claims) value the cells now display.
    const flagChanged = prevFlagRef.current !== showEncountersTotals;
    prevFlagRef.current = showEncountersTotals;

    if (
      (!hasLoaded.current || flagChanged) &&
      columnIds?.length > 0 &&
      isInitialized
    ) {
      hasLoaded.current = true;
      const firstColumnId = columnIds[0];

      let sort_field = null;
      if (apiVolumeFilterFormat && firstColumnId) {
        // Use the full composite key to lookup in apiVolumeFilterFormat
        sort_field = resolveVolumeSortField(
          apiVolumeFilterFormat[firstColumnId],
          showEncountersTotals
        );
      }

      const newSortModel = [{ field: firstColumnId.toString(), sort: 'desc' }];
      sortModelRef.current = newSortModel;
      setSort({
        sort: 'volume',
        sort_id: null, // Don't set sort_id for volume sorting - code set ID goes in filter_set
        sort_field: sort_field,
        order: 'desc',
        sortModel: newSortModel, // Set initial sort model
      });
    } else if (columnIdsChanged && editingColumnId && columnIds?.length > 0) {
      // We just applied an edit (editingColumnId is still set and columnIds changed)
      // Find the new column ID that replaced the editingColumnId
      let editedColumnId: string | null = null;

      // The edited column is the one that's different at the position where editingColumnId was
      const oldIndex = previousColumnIdsRef.current.indexOf(editingColumnId);
      if (oldIndex >= 0 && oldIndex < columnIds.length) {
        // The edited column should be at the same position but with a different ID
        const newColumnAtPosition = columnIds[oldIndex];
        if (newColumnAtPosition !== editingColumnId) {
          editedColumnId = newColumnAtPosition;
        }
      }

      // If we couldn't find it by position, look for any new column ID
      if (!editedColumnId) {
        editedColumnId =
          columnIds.find((id) => !previousColumnIdsRef.current.includes(id)) ||
          null;
      }

      if (editedColumnId) {
        // Set sort to the edited column
        let sort_field = null;
        if (apiVolumeFilterFormat && editedColumnId) {
          sort_field = resolveVolumeSortField(
            apiVolumeFilterFormat[editedColumnId],
            showEncountersTotals
          );
        }

        const editedSortModel = [{ field: editedColumnId.toString(), sort: 'desc' }];
        sortModelRef.current = editedSortModel;
        setSort({
          sort: 'volume',
          sort_id: null, // Don't set sort_id for volume sorting - code set ID goes in filter_set
          sort_field: sort_field,
          order: 'desc',
          sortModel: editedSortModel, // Set sort model to edited column
        });

        // Apply sort to the DataGrid
        if (apiRef.current) {
          apiRef.current.setSortModel([
            {
              field: editedColumnId.toString(),
              sort: 'desc' as GridSortDirection,
            },
          ]);
        }
      }
    } else if (columnIds?.length === 0) {
      hasLoaded.current = null;
    }

    // Always update the previous columnIds reference
    if (columnIds) {
      previousColumnIdsRef.current = [...columnIds];
    }
  }, [
    columnIds,
    apiVolumeFilterFormat,
    showEncountersTotals,
    isInitialized,
    editingColumnId,
    apiRef,
  ]);

  useEffect(() => {
    if (!apiRef.current?.getSelectedRows) return;
    setNewProspects(Array.from(apiRef.current.getSelectedRows().values()));

    return () => setNewProspects([]);
  }, [selectionModel]);

  // No need for scrollbar detection since we're hiding the scrollbar

  const [columnVisibilityModel, setColumnVisibilityModel] = useLocalStorage(
    'providerTable',
    {
      name: true,
      crm_links: true,
      specialty: true,
      soc_type: true,
      systems: false,
      rep_name: false,
      tags: false,
      npi: true,
      ccn: false,
      site_id: false,
      address: false,
      street_address: false,
      secondary_addresses_v2: false,
      city: false,
      state: false,
      zip_code: false,
      affiliations: false,
      groups: false,
      actions: false,
      phone: false,
      email: false,
      created_at: false,
    }
  );

  const handlePageChange = (e, newPage) => {
    setPaginationModel((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (e) => {
    setPaginationModel((prev) => ({
      ...prev,
      pageSize: parseInt(e.target.value, 10),
      page: 0,
    }));
  };

  // Memoize tableParams to prevent re-renders
  const tableParams = useMemo(
    () => ({
      sort: sort.sort,
      sort_id: sort.sort_id,
      sort_field: sort.sort_field,
      order: sort.order,
      // Don't include sortModel in API params
      q: debounceSearch || null,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    }),
    [
      sort.sort,
      sort.sort_id,
      sort.sort_field,
      sort.order,
      debounceSearch,
      paginationModel.page,
      paginationModel.pageSize,
    ]
  );

  // Build query params for API call
  const queryParams = useMemo(() => {
    let query = getSearchQueryParamsForAPI(tableParams);

    const filterSetIds = apiFilterFormat
      .map(
        (id) => `&${encodeURIComponent('filter_set')}=${encodeURIComponent(id)}`
      )
      .join('');

    query += filterSetIds;
    return query;
  }, [tableParams, apiFilterFormat]);

  // Build body data for API call
  const bodyData = useMemo(
    () => ({
      ...apiVolumeFilterFormat,
    }),
    [apiVolumeFilterFormat]
  );

  // RTK Query - fetch list members (currentData preserves previous results during refetch)
  const { data: listMembers, isFetching } = useGetProviderListMembersQuery(
    {
      listId: selectedList?.id,
      queryParams,
      data: bodyData,
    },
    {
      skip: !selectedList?.id,
    }
  );

  // Share the selected providers (IDs and count only) with Scout.
  const scoutSelection = useMemo(() => {
    const selected = new Set((selectionModel ?? []).map(String));
    const rows: { id?: GridRowId; provider_id?: string | number }[] =
      listMembers?.results ?? EMPTY_ROWS;
    return toScoutSelection(
      rows
        .filter((row) => row?.id != null && selected.has(String(row.id)))
        .map((row) => row.provider_id ?? row.id)
    );
  }, [listMembers, selectionModel]);
  useScoutContext(
    SCOUT_CONTEXT_KEYS.LISTS_SELECTION,
    'Selected list providers',
    scoutSelection
  );

  // CCN / Site ID columns are only relevant when the list contains a site of care.
  const hasSites = useMemo(
    () => hasSitesOfCare(listMembers?.results),
    [listMembers]
  );

  // Export list callback
  const exportList = useCallback(
    async ({
      fileFormat,
      columns,
      fileName,
    }: {
      fileFormat: string;
      columns: string[];
      fileName: string;
    }) => {
      await exportMembers({
        listId: selectedList?.id,
        queryParams,
        data: bodyData,
        fileFormat,
        columns,
        fileName,
      }).unwrap();
    },
    [selectedList?.id, queryParams, bodyData, exportMembers]
  );

  // maintain column visibility state
  useEffect(() => {
    // Don't return early - we need to set visibility even if columns aren't loaded yet

    // Use a callback to ensure we're working with the latest state
    setColumnVisibilityModel((columnVisibilityState) => {
      const newVisibility = {
        ...columnVisibilityState,
        ...serializeColumnVisibility(),
      };

      // Force visibility for volume columns when they exist
      if (columnIds?.length > 0) {
        columnIds.forEach((id) => {
          newVisibility[id] = true;
        });
      }

      return newVisibility;
    });
  }, [
    volumeColumns,
    optionColumns,
    columnIds,
    currentYears,
    isTrending,
    hasSites,
  ]);

  const serializeColumnVisibility = useCallback(() => {
    // Process volume columns
    const volumeVisibility = volumeColumns.reduce((acc, col) => {
      if (col.field === 'crm') return acc;

      // If columnIds is empty, hide all volume columns
      if (!columnIds?.length) {
        acc[col.field] = false;
        return acc;
      }

      // Show volume columns only if their field matches an ID in columnIds
      const isVisible = columnIds.includes(col.field);
      acc[col.field] = isVisible;
      return acc;
    }, {});

    // Process option (trending) columns
    const optionVisibility = optionColumns.reduce((acc, col) => {
      const [, year] = col.field.split('_');
      const isValidYear = currentYears.includes(Number(year));
      const isInOptions = VOLUME_SELECT_OPTIONS?.some((o) => o.value === year);

      const isVisible =
        isInOptions && isValidYear && isTrending && columnIds?.length > 0;
      acc[col.field] = isVisible;
      return acc;
    }, {});

    // Combine all visibility settings
    const result = {
      ...volumeVisibility,
      ...optionVisibility,
      crm: crmTypes?.length > 0,
      ccn: hasSites,
      site_id: hasSites,
    };

    return result;
  }, [
    volumeColumns,
    optionColumns,
    currentYears,
    crmTypes,
    columnIds,
    isTrending,
    hasSites,
  ]);

  const handleExportList = async (type: string) => {
    // Get basic visible columns but filter out volume/trend UI columns
    const volumeKeySet = new Set(Object.keys(apiVolumeFilterFormat || {}));
    const basicColumns = apiRef.current
      .getVisibleColumns()
      .map((col) => col.field)
      .filter(
        (col) =>
          col.indexOf('__') === -1 &&
          !col.startsWith('option_') &&
          col !== 'volume_drawer' &&
          !volumeKeySet.has(col) // exclude composite volume column keys
      );

    // Build volume columns in the user's configured order (columnIds order)
    const volumeColumns: string[] = [];
    const seenTypes = new Set<string>();
    columnIds.forEach((id) => {
      const type = apiVolumeFilterFormat?.[id]?.type;
      if (type && !seenTypes.has(type)) {
        seenTypes.add(type);
        volumeColumns.push(type);
      }
    });

    // Add trends in the same user-configured type order
    if (isTrending) {
      const seenTrendTypes = new Set<string>();
      columnIds.forEach((id) => {
        const type = apiVolumeFilterFormat?.[id]?.type;
        if (type && !seenTrendTypes.has(type)) {
          seenTrendTypes.add(type);
          volumeColumns.push(`${type}_trends`);
        }
      });
    }

    const visibleColumns = basicColumns.concat(volumeColumns);

    // If email is visible, add email_source before it
    const emailIndex = visibleColumns.indexOf('email');
    if (emailIndex !== -1) {
      visibleColumns.splice(emailIndex, 0, 'email_source');
    }

    // Include crm_status if MedRM is enabled
    const isMedRM = crmTypes?.[0]?.toLowerCase() === 'medrm';
    if (isMedRM) {
      visibleColumns.push('crm_status');
    }

    setIsExporting(true);
    try {
      await exportList({
        fileFormat: type,
        columns: visibleColumns,
        fileName: `${selectedList.name}`,
      });
      setNotification({
        title: 'Success',
        message:
          'Export initiated - You will receive an email when it is ready for download',
        type: 'success',
      });
      log.event('Export', {
        type: LOCATION.LISTS,
        source: `${selectedList.name} - List`,
        columns: visibleColumns,
        count: selectedList?.num_items,
      });
    } catch (err) {
      log.exception(`Error exporting List: ${err}`);

      setNotification({
        title: 'Error',
        message: err?.error_message || 'Error exporting, please try again.',
        type: 'error',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleSortChange = useCallback(
    (model) => {
      // Early return if model hasn't changed
      if (JSON.stringify(model) === JSON.stringify(sortModelRef.current)) return;
      sortModelRef.current = model;

      const { field, sort: sortDirection } = model?.[0] || {};
      const isVolumeColumn = field && columnIds.includes(field);

      // Helper to get volume sort field type
      const getVolumeSortField = (field) => {
        if (!field || !apiVolumeFilterFormat) return null;
        const baseId = extractBaseId(field);
        const compositeKey = Object.keys(apiVolumeFilterFormat).find(
          (key) => extractBaseId(key) === baseId
        );
        return resolveVolumeSortField(
          apiVolumeFilterFormat[compositeKey],
          showEncountersTotals
        );
      };

      // Determine sort parameters
      let sortValue, sort_field, sort_id;
      if (model?.length && isVolumeColumn) {
        sortValue = 'volume';
        sort_field = getVolumeSortField(field);
        sort_id = field; // Use the full composite key as sort_id
      } else if (!model?.length && columnIds.length > 0) {
        // Fallback to first volume column when model is empty
        sortValue = 'volume';
        sort_field = resolveVolumeSortField(
          apiVolumeFilterFormat[columnIds[0]],
          showEncountersTotals
        );
        sort_id = columnIds[0]; // Use the full composite key as sort_id
      } else {
        sortValue = field || 'name';
        sort_field = null;
        sort_id = null;
      }

      setSort({
        sort: sortValue,
        sort_id: sort_id, // Use the full composite key for volume columns
        sort_field,
        order: sortDirection || 'desc',
        sortModel: model,
      });
    },
    [columnIds, apiVolumeFilterFormat, showEncountersTotals]
  );

  const handleSearch = (value) => {
    setSearch(value);
    // ensure we are on the 1st page for searches
    if (paginationModel.page !== 0) {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  };

  // Create secondary table columns with dynamic volume column sizing and drawer column
  const secondaryTableColumns = useMemo(() => {
    return createSecondaryTableColumns(LOCATION.LISTS);
  }, [createSecondaryTableColumns]);

  // Get only volume, option, and empty column fields for pinning to the right
  const rightPinnedFields = useMemo(() => {
    // Create a map of volume columns by field for quick lookup
    const volumeColumnsMap = volumeColumns.reduce((acc, col) => {
      acc[col.field] = col;
      return acc;
    }, {});

    // Get volume column fields in the exact order of columnIds
    const volumeFields =
      columnIds && columnIds.length > 0
        ? columnIds
            .map((id) => id.toString())
            .filter((id) => volumeColumnsMap[id])
        : [];

    // Get option column fields (trend columns)
    const optionFields = optionColumns
      .filter(() => isTrending && columnIds.length > 0)
      .map((col) => col.field);

    // Add the empty drawer column
    const drawerField = ['volume_drawer'];

    // Combine all fields that should be pinned to the right
    return [...volumeFields, ...optionFields, ...drawerField];
  }, [volumeColumns, optionColumns, columnIds, isTrending]);

  const combinedColumns = useMemo(() => {
    // Add proper sortingOrder to regular columns and remove custom renderHeader to fix sorting
    const configuredListColumns = listTableColumns.map((col) => ({
      ...col,
      sortingOrder: ['asc', 'desc'] as any, // Regular columns: remove null state to prevent third click reset
      renderHeader: undefined, // Remove custom header to restore default DataGrid sorting behavior
    }));

    // Reverse the order of columns to help with pinning
    return [...secondaryTableColumns, ...configuredListColumns];
  }, [secondaryTableColumns, listTableColumns]);

  const userCanExport = auth.hasPermission('export.provider_lists.all');

  return (
    <div className="relative flex flex-col p-0 bottom-0 h-full w-full min-w-0 overflow-hidden">
      <div className="w-full h-full overflow-hidden flex flex-col">
        <DataGridPremium
          disableVirtualization
          apiRef={apiRef}
          className="pin-all-columns-right"
          rows={listMembers?.results || EMPTY_ROWS}
          rowCount={listMembers?.count || 0}
          columns={combinedColumns}
          disableColumnMenu
          hideFooter={true}
          checkboxSelection
          disableRowSelectionOnClick
          pinnedColumns={{ right: rightPinnedFields }}
          sx={{
            // Base table styling
            border: 'none',
            borderRadius: 0,
            overflow: 'hidden',
            height: '100%',
            flex: 1,

            // Fix for pinned columns alignment
            '& .MuiDataGrid-columnHeaders': {
              position: 'relative', // Ensure headers have a positioning context
              paddingRight: '0px',
              '& .MuiDataGrid-columnSeparator': {
                color: 'none',
              },
            },

            // Pinned column headers (v7: native sticky, no position overrides)
            '& .MuiDataGrid-columnHeader--pinnedRight': {
              background: 'var(--secondary-outlined-ActiveBg, #D6F6F1)',
            },

            // Pinned cells (v7: native sticky, no position overrides)
            '& .MuiDataGrid-cell--pinnedRight': {
              background: 'var(--secondary-outlined-ActiveBg, #D6F6F1)',
            },

            // Virtual scroller
            '& .MuiDataGrid-virtualScroller': {
              overflow: 'auto', // Keep default overflow behavior
              overflowX: 'auto', // Allow horizontal scrolling
              minHeight: '300px', // Ensure minimum height even when empty
            },

            // Empty state styling
            '& .MuiDataGrid-overlay': {
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },

            // Primary columns hover effect
            '& .MuiDataGrid-row:hover, & .Mui-hovered': {
              backgroundColor: 'rgba(204, 204, 194, 0.13)',
              '&.Mui-hovered': {
                backgroundColor: 'rgba(204, 204, 194, 0.13)',
              },
            },

            // Secondary columns - base styling
            '& .secondary-column-header, & .secondary-column-cell': {
              background: 'var(--secondary-outlined-ActiveBg, #D6F6F1)',
              borderRight: '#00C6A9 1px solid',
            },

            // Secondary column headers specific styling
            '& .secondary-column-header': {
              borderBottom: '#00C6A9 1px solid !important', // Restored 2px with !important to ensure visibility
              boxShadow: 'none',
            },

            // Override any conflicting styles from MUI
            '& .MuiDataGrid-columnHeaders .secondary-column-header, & .MuiDataGrid-columnHeadersInner .secondary-column-header':
              {
                borderBottom: '#00C6A9 1px solid !important', // Ensure this is not overridden
              },

            // Secondary column cells specific styling
            '& .secondary-column-cell': {
              borderBottom: '#00C6A9 1px solid',
              borderLeft: columnIds.length === 0 ? '#00C6A9 1px solid' : 'none',
            },

            // First column in secondary section gets left border
            '& .secondary-first-column-header, & .secondary-first-column-cell':
              {
                borderLeft: columnIds.length > 0 ? '#00C6A9 1px solid' : 'none',
              },

            // Add left border to the first pinned column header and cell
            '& .MuiDataGrid-columnHeader--pinnedRight:first-of-type, & .MuiDataGrid-cell--pinnedRight:first-of-type':
              {
                borderLeft: '#00C6A9 1px solid',
              },

            // Column separators in secondary section
            '& .secondary-column-header .MuiDataGrid-columnSeparator': {
              color: '#00C6A9',
            },

            // Hover effect for secondary columns
            '& .MuiDataGrid-row:hover .secondary-column-cell, & .Mui-hovered .secondary-column-cell':
              {
                backgroundColor: '#D6F6F1',
              },

            // Hover effect for pinned cells
            '& .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedRight, & .Mui-hovered .MuiDataGrid-cell--pinnedRight':
              {
                backgroundColor: '#D6F6F1',
              },
          }}
          initialState={{
            columns: { columnVisibilityModel },
            pagination: { paginationModel: { page: 0, pageSize: 50 } },
            sorting: { sortModel: initialSortModel },
          }}
          slots={{
            toolbar: EncountersToolbar,
            baseCheckbox: TableCheckbox,
            columnMenu: CustomColumnMenu,
            loadingOverlay: () => (
              <div className="flex items-center justify-center h-full backdrop-blur-sm">
                <CircularProgress size="sm" color="neutral" />
              </div>
            ),
            noRowsOverlay: () => (
              <NewNoVolumeOverlay
                columnIds={columnIds
                  .map((id) => {
                    const baseId = id.split('_')[0];
                    return parseInt(baseId, 10);
                  })
                  .filter((id) => !isNaN(id))}
              />
            ),
            ...TableSettings.slots,
          }}
          slotProps={{
            columnsManagement: {
              disableShowHideToggle: true,
            },
            toolbar: {
              location: LOCATION.LISTS,
              onSearch: handleSearch,
              // Export button props
              onExport: handleExportList,
              isExporting: isExporting,
              // Column button props
              columns: listTableColumns,
              columnVisibilityModel: columnVisibilityModel,
              setColumnVisibility: setColumnVisibilityModel,
              // Feature flags
              showExportButton: userCanExport,
              showDeleteButton: true,
              showMoveToListButton: true,
            },
          }}
          // Pagination
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          // Sorting
          sortingMode="server"
          sortModel={sort.sortModel || []}
          onSortModelChange={handleSortChange}
          // Column visibility
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          // Rows
          loading={isFetching}
          rowHeight={75}
          onRowSelectionModelChange={setSelectionModel}
          rowSelectionModel={selectionModel}
        />
      </div>
      <TableFooter
        page={paginationModel.page}
        pageSize={paginationModel.pageSize}
        rowCount={listMembers?.count || 0}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
      <ListsUploaderDrawer />
      <TableVolumeDrawer
        location={LOCATION.LISTS}
        showReferringTreating
        showBilling
        showTrending
      />
      <EditFilterDrawer location={LOCATION.LISTS} />
    </div>
  );
};

const CustomColumnMenu = (props: GridColumnMenuProps) => {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        columnMenuFilterItem: null,
        columnMenuAggregationItem: null,
        columnMenuGroupingItem: null,
      }}
    />
  );
};

export default NewListsTable;
